import React, { useEffect, useMemo, useState } from 'react';
import { BarChart2, Download, X, CheckCircle2, CircleDot, Lock, PlayCircle } from 'lucide-react';
import { learningApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { mins } from './MyLearning';

/**
 * Learning Report (admin): every person and course assigned to them —
 * features learned, time spent learning, last activity, due and overdue —
 * and, for one person, each feature and every sitting.
 */
const dmy = (s: any) => (s ? String(s).slice(0, 10).split('-').reverse().join('-') : '—');

const LearningReport: React.FC = () => {
  const { showError } = useToast();
  const [data, setData] = useState<{ courses: any[]; rows: any[] } | null>(null);
  const [course, setCourse] = useState('');
  const [team, setTeam] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<any | null>(null);

  useEffect(() => {
    learningApi.report().then(r => setData(r.data)).catch(e => showError('Could not load the report', e?.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => (data?.rows || []).filter(r =>
    (!course || String(r.course_id) === course) && (!team || r.team === team) &&
    (!search || String(r.user).toLowerCase().includes(search.toLowerCase()))), [data, course, team, search]);

  const openDetail = async (r: any) => {
    try { setDetail((await learningApi.reportDetail(r.user_id, r.course_id)).data); }
    catch (e: any) { showError('Could not open', e?.message); }
  };
  const exportExcel = async () => {
    const { utils, writeFile } = await import('xlsx');
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rows.map(r => ({
      Staff: r.user, Team: r.team, Course: r.course, 'Features learned': r.learned, Features: r.features, 'Progress %': r.percent,
      'Learning minutes': Math.round(r.watched_seconds / 60), 'Last activity': r.last_activity_at, Completed: r.completed_at, Due: r.due_date, Overdue: r.overdue ? 'Yes' : '',
    }))), 'Learning');
    writeFile(wb, `Learning_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const done = rows.filter(r => r.features && r.learned >= r.features).length;

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1300px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <BarChart2 className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800 flex-1">Learning Report</h1>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Staff name" className="px-2 py-1.5 text-sm border border-gray-300 rounded w-44" />
          <select value={course} onChange={e => setCourse(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded">
            <option value="">All courses</option>
            {(data?.courses || []).map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select value={team} onChange={e => setTeam(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded">
            <option value="">All teams</option><option>Service</option><option>Marketing</option><option>Both</option>
          </select>
          <button onClick={exportExcel} disabled={!rows.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"><Download className="h-3.5 w-3.5" /> Excel</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {[
            ['Assigned', rows.length, ''],
            ['Completed', done, 'text-emerald-700'],
            ['In progress', rows.filter(r => r.learned < r.features && (r.learned || r.in_progress)).length, 'text-amber-700'],
            ['Overdue', rows.filter(r => r.overdue).length, 'text-red-700'],
          ].map(([l, v, c]) => (
            <div key={l as string} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-[11px] font-bold uppercase text-gray-500">{l}</div>
              <div className={`text-2xl font-bold ${c}`}>{v}</div>
            </div>
          ))}
        </div>
        <div className="overflow-auto border border-gray-200 rounded max-h-[calc(100vh-260px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left">Staff</th><th className="px-2 py-2 text-left">Course</th><th className="px-2 py-2 text-left w-56">Progress</th>
                <th className="px-2 py-2 text-right">Learning time</th><th className="px-2 py-2 text-left">Last activity</th>
                <th className="px-2 py-2 text-left">Completed</th><th className="px-2 py-2 text-left">Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!data ? <tr><td colSpan={7} className="p-8 text-center text-gray-400">Loading…</td></tr>
                : rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-400">No courses assigned yet.</td></tr>
                : rows.map(r => (
                  <tr key={r.user_id + '-' + r.course_id} onClick={() => openDetail(r)} className={`cursor-pointer hover:bg-blue-50/50 ${r.overdue ? 'bg-red-50/50' : ''}`}>
                    <td className="px-2 py-1.5 font-medium text-gray-800">{r.user}{r.team ? <span className="text-[11px] text-gray-400"> · {r.team}</span> : null}</td>
                    <td className="px-2 py-1.5">{r.course}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${r.percent >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${r.percent}%` }} /></div>
                        <span className="text-xs tabular-nums text-gray-600 w-16 text-right">{r.learned}/{r.features}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.watched_seconds ? mins(r.watched_seconds) : '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{r.last_activity_at ? String(r.last_activity_at).slice(0, 16) : 'not started'}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.completed_at ? <span className="text-emerald-700">{dmy(r.completed_at)}</span> : '—'}</td>
                    <td className={`px-2 py-1.5 whitespace-nowrap ${r.overdue ? 'text-red-600 font-semibold' : ''}`}>{dmy(r.due_date)}{r.overdue ? ' overdue' : ''}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-5 max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-2 mb-3">
              <h3 className="font-semibold text-gray-800 flex-1">{detail.user?.name} — {detail.course?.title}</h3>
              <button onClick={() => setDetail(null)} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
            </div>
            <table className="w-full text-sm border border-gray-200 rounded mb-4">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500"><tr><th className="px-2 py-1.5 text-left">Feature</th><th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-right">Watched</th><th className="px-2 py-1.5 text-right">Video</th><th className="px-2 py-1.5 text-right">Sittings</th><th className="px-2 py-1.5 text-left">Learned on</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(detail.course?.features || []).map((f: any, i: number) => {
                  const Icon = f.status === 'learned' ? CheckCircle2 : f.locked ? Lock : f.status === 'in_progress' ? CircleDot : PlayCircle;
                  return (
                    <tr key={f.id}>
                      <td className="px-2 py-1.5">{i + 1}. {f.title}</td>
                      <td className="px-2 py-1.5"><span className="inline-flex items-center gap-1"><Icon className={`h-3.5 w-3.5 ${f.status === 'learned' ? 'text-emerald-600' : f.status === 'in_progress' ? 'text-amber-500' : 'text-gray-400'}`} />{f.status === 'learned' ? 'Learned' : f.status === 'in_progress' ? 'In progress' : f.locked ? 'Locked' : 'Not started'}</span></td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{f.watched_seconds ? mins(f.watched_seconds) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">{f.duration_sec ? mins(f.duration_sec) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{f.sessions || '—'}</td>
                      <td className="px-2 py-1.5">{f.completed_at ? String(f.completed_at).slice(0, 16) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[11px] font-bold uppercase text-gray-500 mb-1">Every sitting</div>
            <table className="w-full text-xs border border-gray-200 rounded">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500"><tr><th className="px-2 py-1 text-left">Started</th><th className="px-2 py-1 text-left">Ended</th><th className="px-2 py-1 text-left">Feature</th><th className="px-2 py-1 text-right">Watched</th><th className="px-2 py-1 text-right">From → to (min)</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {detail.sessions.length === 0 ? <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-400">No sittings yet.</td></tr>
                  : detail.sessions.map((s: any, i: number) => (
                    <tr key={i}><td className="px-2 py-1 whitespace-nowrap">{s.started_at}</td><td className="px-2 py-1 whitespace-nowrap">{s.ended_at || '—'}</td><td className="px-2 py-1">{s.title}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{mins(s.watched_seconds)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{Math.floor((s.start_position || 0) / 60)} → {s.end_position !== null ? Math.floor(s.end_position / 60) : '—'}</td></tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningReport;
